/**
 * Shared page chrome: header (breadcrumb, title, subtitle, primary action)
 * and empty state. Pages compose these instead of hand-rolling headings so
 * every section reads the same way.
 *
 * The breadcrumb defaults to the page's section (from ../lib/navigation.ts);
 * pass `breadcrumb` for detail pages that sit below a list.
 */
import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { resolveNav, tabPath, workspacePath } from "../lib/navigation";
import { useWorkspace } from "../hooks/useWorkspace";

export interface Crumb { label: string; to?: string }

export function PageHeader({ title, subtitle, actions, breadcrumb, children }: {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Primary (and secondary) actions, right-aligned on wide screens. */
  actions?: ReactNode;
  /** Defaults to the owning section; pass [] to hide. */
  breadcrumb?: Crumb[];
  /** Extra header content below the subtitle (filters summary, links). */
  children?: ReactNode;
}) {
  const location = useLocation();
  const { current } = useWorkspace();
  const nav = resolveNav(location.pathname, location.search);
  const crumbs: Crumb[] = breadcrumb ?? (nav ? [{ label: nav.section.label, to: tabPath(nav.section.tabs[0]) }] : []);
  return (
    <header className="cmcp-page-header">
      <div className="min-w-0 flex-1">
        {crumbs.length > 0 && (
          <nav aria-label="Breadcrumb" className="cmcp-breadcrumb">
            {crumbs.map((crumb, index) => (
              <span key={`${crumb.label}-${index}`}>
                {index > 0 && <span aria-hidden="true" className="cmcp-breadcrumb-sep">/</span>}
                {crumb.to ? <Link to={workspacePath(crumb.to, current?.id)}>{crumb.label}</Link> : <span>{crumb.label}</span>}
              </span>
            ))}
          </nav>
        )}
        <h1 className="cmcp-page-title">{title}</h1>
        {subtitle && <p className="cmcp-page-subtitle">{subtitle}</p>}
        {children}
      </div>
      {actions && <div className="cmcp-page-actions">{actions}</div>}
    </header>
  );
}

export function EmptyState({ title, children, action, icon }: {
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="cmcp-empty">
      {icon && <div className="cmcp-empty-icon" aria-hidden="true">{icon}</div>}
      <p className="cmcp-empty-title">{title}</p>
      {children && <div className="cmcp-empty-body">{children}</div>}
      {action && <div className="cmcp-empty-action">{action}</div>}
    </div>
  );
}
